import { useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, FileText, FileType2, MoreHorizontal, Palette, Pencil, PenLine, Pill, Plus, Share2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type {
  LinkCompartilhadoResponse,
  ReceituarioResponse,
  ReceituarioResponseStatus,
  ReceituariosControllerListarTipo,
} from '@/api/generated/model';
import {
  getReceituariosControllerListarQueryKey,
  getReceituariosControllerPdfUrl,
  useReceituariosControllerAssinar,
  useReceituariosControllerCompartilhar,
  useReceituariosControllerCompartilharPorEmail,
  useReceituariosControllerExcluir,
  useReceituariosControllerListar,
} from '@/api/generated/receituarios/receituarios';
import { ApiError, urlDeArquivo } from '@/api/http-client';
import { LayoutDocumentoDialogo } from '@/features/documentos/components/LayoutDocumentoDialogo';
import { SeloDeAssinaturaDigital } from '@/features/documentos/components/SeloDeAssinaturaDigital';
import { useSenhaDoCertificado } from '@/features/documentos/lib/useSenhaDoCertificado';
import { ReceituarioFormDialogo } from '@/features/receituarios/components/ReceituarioFormDialogo';
import { TemplatesDialogo } from '@/features/receituarios/components/TemplatesDialogo';
import { CLASSE_STATUS, ROTULO_STATUS, ROTULO_TIPO, STATUS, TIPOS } from '@/features/receituarios/rotulos';
import { CompartilharDialogo } from '@/shared/components/CompartilharDialogo';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { ROTAS } from '@/shared/lib/rotas';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const TODOS = 'todos';

/**
 * Receituários (F4-1).
 *
 * Mesmo ciclo da requisição — rascunho, assinar, PDF, compartilhar — com duas
 * coisas a mais: modelos de texto e importação de produto de parceiro.
 *
 * ⚠️ Assinar é irreversível: depois disso o documento é imutável e o PDF passa
 * a usar o snapshot congelado. Compartilhar só funciona depois de assinar.
 */
export function ReceituariosPage() {
  const { pode } = usePermissoes();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState<string>(TODOS);
  const [tipo, setTipo] = useState<string>(TODOS);
  const [formAberto, setFormAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<ReceituarioResponse | undefined>();
  const [templatesAberto, setTemplatesAberto] = useState(false);
  const [layoutAberto, setLayoutAberto] = useState(false);
  const [aAssinar, setAAssinar] = useState<ReceituarioResponse | null>(null);
  const [aExcluir, setAExcluir] = useState<ReceituarioResponse | null>(null);
  const [semSncr, setSemSncr] = useState<{ receituario: ReceituarioResponse; mensagem: string } | null>(null);
  /**
   * O documento vai junto do link: o diálogo precisa do paciente e do
   * veterinário para montar o texto do WhatsApp e do e-mail, e a listagem é a
   * única que os tem à mão.
   */
  const [compartilhando, setCompartilhando] = useState<{
    documento: ReceituarioResponse;
    link: LinkCompartilhadoResponse;
  } | null>(null);

  const { data, isPending, isError, refetch } = useReceituariosControllerListar({
    pagina,
    porPagina: 20,
    ordenarPor: 'criadoEm',
    ordem: 'desc',
    ...(busca.trim() ? { busca: busca.trim() } : {}),
    ...(status !== TODOS ? { status: status as ReceituarioResponseStatus } : {}),
    ...(tipo !== TODOS ? { tipo: tipo as ReceituariosControllerListarTipo } : {}),
  });

  const assinar = useReceituariosControllerAssinar();
  const { comSenhaSeNecessario, dialogo: dialogoDeSenha } = useSenhaDoCertificado();
  const compartilhar = useReceituariosControllerCompartilhar();
  const compartilharPorEmail = useReceituariosControllerCompartilharPorEmail();
  const excluir = useReceituariosControllerExcluir();

  const podeCriar = pode('receituarios.criar');
  const podeEditar = pode('receituarios.editar');
  const podeExcluir = pode('receituarios.excluir');
  const podeAssinar = pode('receituarios.assinar');

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  async function confirmarAssinatura(receituario: ReceituarioResponse) {
    try {
      const assinado = await comSenhaSeNecessario((senhaCertificado) =>
        assinar.mutateAsync({
          id: receituario.id,
          ...(senhaCertificado ? { data: { senhaCertificado } } : {}),
        }),
      );
      if (!assinado) return;

      await queryClient.invalidateQueries({ queryKey: getReceituariosControllerListarQueryKey() });
      toast.success(
        assinado.assinaturaDigital ? 'Receituário assinado digitalmente.' : 'Receituário assinado.',
      );
    } catch (erro) {
      // Controle especial sem endereço: a mensagem do servidor já diz o que
      // falta; o botão leva direto para onde se corrige.
      if (erro instanceof ApiError && erro.codigo === 'ENDERECO_DO_EMITENTE_AUSENTE') {
        toast.error(erro.message, {
          action: { label: 'Abrir perfil', onClick: () => navigate(ROTAS.perfil) },
        });
        return;
      }
      // Sem o número do SNCR: pop-up no centro (um toast passa despercebido) e
      // o botão abre o próprio receituário, onde o número é informado.
      if (erro instanceof ApiError && erro.codigo === 'NUMERO_SNCR_AUSENTE') {
        setSemSncr({ receituario, mensagem: erro.message });
        return;
      }
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível assinar.');
    } finally {
      setAAssinar(null);
    }
  }

  async function gerarLink(receituario: ReceituarioResponse) {
    try {
      setCompartilhando({
        documento: receituario,
        link: await compartilhar.mutateAsync({ id: receituario.id, data: {} }),
      });
    } catch (erro) {
      // 409 é o caso previsto: ainda não assinado. A mensagem do servidor é
      // genérica; aqui dizemos o que fazer a respeito.
      if (erro instanceof ApiError && erro.status === 409) {
        toast.error('Assine o receituário antes de compartilhar.');
        return;
      }
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível gerar o link.');
    }
  }

  async function confirmarExclusao(receituario: ReceituarioResponse) {
    try {
      await excluir.mutateAsync({ id: receituario.id });
      await queryClient.invalidateQueries({ queryKey: getReceituariosControllerListarQueryKey() });
      toast.success('Receituário excluído.');
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível excluir.');
    } finally {
      setAExcluir(null);
    }
  }

  function abrirPdf(receituario: ReceituarioResponse) {
    // Não passa pelo cliente gerado de propósito — ver `urlDeArquivo`.
    window.open(urlDeArquivo(getReceituariosControllerPdfUrl(receituario.id)), '_blank', 'noopener');
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <Input
            value={busca}
            onChange={(e) => aoFiltrar(() => setBusca(e.target.value))}
            placeholder="Buscar por número ou paciente..."
            className="sm:max-w-xs"
          />

          <Select value={status} onValueChange={(v) => aoFiltrar(() => setStatus(v))}>
            <SelectTrigger className="sm:w-44" aria-label="Filtrar por status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos os status</SelectItem>
              {STATUS.map((s) => (
                <SelectItem key={s} value={s}>
                  {ROTULO_STATUS[s] ?? s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={tipo} onValueChange={(v) => aoFiltrar(() => setTipo(v))}>
            <SelectTrigger className="sm:w-48" aria-label="Filtrar por tipo">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos os tipos</SelectItem>
              {TIPOS.map((t) => (
                <SelectItem key={t} value={t}>
                  {ROTULO_TIPO[t] ?? t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* `flex-wrap`: três botões estouram a linha no celular. */}
        <div className="flex flex-wrap gap-2">
          {/* ⚠️ "Modelos" (textos salvos) e "Configuração de layout" (aparência
              do papel) ficam lado a lado e são confundíveis. Os ícones
              distintos e a descrição dentro de cada diálogo é que separam os
              dois — renomear qualquer um quebraria o vocabulário já
              estabelecido em `TemplatesDialogo`. */}
          <Button variant="outline" onClick={() => setLayoutAberto(true)}>
            <Palette className="w-4 h-4" />
            Configuração de layout
          </Button>

          <Button variant="outline" onClick={() => setTemplatesAberto(true)}>
            <FileType2 className="w-4 h-4" />
            Modelos
          </Button>

          {podeCriar && (
            <Button
              onClick={() => {
                setEmEdicao(undefined);
                setFormAberto(true);
              }}
            >
              <Plus className="w-4 h-4" />
              Novo receituário
            </Button>
          )}
        </div>
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">Receituários</h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={Pill}
            titulo="Nenhum receituário encontrado"
            descricao={
              busca || status !== TODOS || tipo !== TODOS
                ? 'Tente outro termo ou limpe os filtros.'
                : 'Crie o primeiro receituário para começar.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Paciente</TableHead>
                    <TableHead>Itens</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Veterinário</TableHead>
                    <TableHead className="w-20">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((receituario) => (
                    <TableRow key={receituario.id}>
                      <TableCell className="font-medium text-foreground whitespace-nowrap">
                        {/* Rascunho ainda não tem número: é atribuído na assinatura. */}
                        {receituario.numero ?? '—'}
                      </TableCell>
                      <TableCell className="text-foreground">
                        {receituario.pacienteNome}
                        {receituario.tipo === 'CONTROLE_ESPECIAL' && (
                          <Badge variant="outline" className="ml-2 border-amber-500/50 text-amber-700 dark:text-amber-300">
                            Controle especial
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-foreground">{receituario.itens.length}</span>
                        {receituario.itens.some((i) => i.produtoParceiroId) && (
                          <Badge variant="outline" className="ml-2">
                            de parceiro
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={CLASSE_STATUS[receituario.status]}>
                          {ROTULO_STATUS[receituario.status] ?? receituario.status}
                        </Badge>
                        {receituario.assinaturaDigital && <SeloDeAssinaturaDigital />}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {receituario.veterinarioNome}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Ações do receituário de ${receituario.pacienteNome}`}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => abrirPdf(receituario)}>
                              <FileText className="w-4 h-4" />
                              {receituario.status === 'ASSINADO' ? 'Abrir PDF' : 'Prévia em PDF'}
                            </DropdownMenuItem>

                            {/* `editavel` vem do servidor — não derivar do status. */}
                            {podeEditar && receituario.editavel && (
                              <DropdownMenuItem
                                onClick={() => {
                                  setEmEdicao(receituario);
                                  setFormAberto(true);
                                }}
                              >
                                <Pencil className="w-4 h-4" />
                                Editar
                              </DropdownMenuItem>
                            )}

                            {podeAssinar && receituario.editavel && (
                              <DropdownMenuItem onClick={() => setAAssinar(receituario)}>
                                <PenLine className="w-4 h-4" />
                                Assinar
                              </DropdownMenuItem>
                            )}

                            {/* A mesma página que o QR do PDF abre — o médico confere o que a farmácia vê. */}
                            {receituario.urlDeVerificacao && (
                              <DropdownMenuItem asChild>
                                <a href={receituario.urlDeVerificacao} target="_blank" rel="noreferrer">
                                  <BadgeCheck className="w-4 h-4" />
                                  Página de verificação
                                </a>
                              </DropdownMenuItem>
                            )}

                            <DropdownMenuItem onClick={() => void gerarLink(receituario)}>
                              <Share2 className="w-4 h-4" />
                              Compartilhar
                            </DropdownMenuItem>

                            {podeExcluir && receituario.editavel && (
                              <DropdownMenuItem onClick={() => setAExcluir(receituario)}>
                                <Trash2 className="w-4 h-4" />
                                Excluir
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>

      <ReceituarioFormDialogo
        aberto={formAberto}
        aoFechar={() => setFormAberto(false)}
        {...(emEdicao ? { receituario: emEdicao } : {})}
      />

      <TemplatesDialogo
        aberto={templatesAberto}
        aoFechar={() => setTemplatesAberto(false)}
        podeEditar={podeCriar}
      />

      <LayoutDocumentoDialogo aberto={layoutAberto} aoFechar={() => setLayoutAberto(false)} />

      {compartilhando && (
        <CompartilharDialogo
          aberto
          aoFechar={() => setCompartilhando(null)}
          link={compartilhando.link}
          tipo="RECEITUARIO"
          pacienteNome={compartilhando.documento.pacienteNome}
          veterinarioNome={compartilhando.documento.veterinarioNome}
          aoEnviarPorEmail={({ email, salvarNoTutor }) =>
            compartilharPorEmail.mutateAsync({
              id: compartilhando.documento.id,
              data: { email, salvarNoTutor },
            })
          }
        />
      )}

      {dialogoDeSenha}

      <AlertDialog open={Boolean(aAssinar)} onOpenChange={(v) => !v && setAAssinar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Assinar este receituário?</AlertDialogTitle>
            <AlertDialogDescription>
              Depois de assinado ele não pode mais ser editado nem excluído, e o PDF passa a
              usar a versão congelada agora. É o que permite compartilhar o documento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => aAssinar && void confirmarAssinatura(aAssinar)}>
              Assinar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(semSncr)} onOpenChange={(v) => !v && setSemSncr(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Número do SNCR obrigatório</AlertDialogTitle>
            <AlertDialogDescription>{semSncr?.mensagem}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Fechar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!semSncr) return;
                setEmEdicao(semSncr.receituario);
                setFormAberto(true);
              }}
            >
              Editar receita
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(aExcluir)} onOpenChange={(v) => !v && setAExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir este receituário?</AlertDialogTitle>
            <AlertDialogDescription>
              {aExcluir && aExcluir.totalVersoes > 1
                ? `As ${aExcluir.totalVersoes} versões gravadas vão junto. Não há como desfazer.`
                : 'O receituário sai do histórico do paciente. Não há como desfazer.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => aExcluir && void confirmarExclusao(aExcluir)}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
